import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DatePicker,
  Drawer,
  Input,
  Field,
  FieldGroup,
  Select
} from '@itds/elements/index.js';
import { STATES } from '@itds/Constants';

import Api from '@carrier/utils/Api';

const Edit = ({ carriers, onClose, onSave, record, show }) => {
  const [data, setData] = useState({});

  useEffect(() => {
    const new_record = record || {};

    setData({
      carrier_id: new_record.carrier_id || '',
      financial_strength_rating_current: new_record.financial_strength_rating_current || '',
      financial_strength_rating_current_effective_date:
        new_record.financial_strength_rating_current_effective_date || '',
      financial_strength_rating_outlook: new_record.financial_strength_rating_outlook || '',
      financial_strength_category: new_record.financial_strength_category || '',
      long_term_issuer_rating_current: new_record.long_term_issuer_rating_current || '',
      long_term_issuer_rating_current_effective_date:
        new_record.long_term_issuer_rating_current_effective_date || '',
      long_term_issuer_rating_current_outlook:
        new_record.long_term_issuer_rating_current_outlook || '',
      ambest_carrier_name: new_record.ambest_carrier_name || '',
      street_address: new_record.street_address || '',
      city: new_record.city || '',
      state: new_record.state || '',
      zip_code: new_record.zip_code || '',
      phone_number: new_record.phone_number || '',
      bonds: new_record.bonds || '',
      mortgage_loans_and_real_estate_percent:
        new_record.mortgage_loans_and_real_estate_percent || '',
      common_and_preferred_stock_percent: new_record.common_and_preferred_stock_percent || '',
      other_assets_percent: new_record.other_assets_percent || '',
      total_assets: new_record.total_assets || '',
      life_reserves_percent: new_record.life_reserves_percent || '',
      health_reserves: new_record.health_reserves || '',
      annuity_reserves_and_deposit_liabilities:
        new_record.annuity_reserves_and_deposit_liabilities || '',
      other_liabilities: new_record.other_liabilities || '',
      capital_and_surplus: new_record.capital_and_surplus || '',
      direct_premiums_written: new_record.direct_premiums_written || '',
      net_premiums_written_and_deposits: new_record.net_premiums_written_and_deposits || '',
      operating_cash_flow: new_record.operating_cash_flow || '',
      nog_before_taxes: new_record.nog_before_taxes || '',
      nog_after_taxes: new_record.nog_after_taxes || '',
      net_income: new_record.net_income || '',
      unrealized_capital_gains: new_record.unrealized_capital_gains || '',
      benefit_paid_to_npw_and_dep: new_record.benefit_paid_to_npw_and_dep || '',
      commissions_and_expenses_to_npw_and_deposit:
        new_record.commissions_and_expenses_to_npw_and_deposit || '',
      nog_to_total_assets: new_record.nog_to_total_assets || '',
      nog_to_total_revenue: new_record.nog_to_total_revenue || '',
      operating_return_on_equity: new_record.operating_return_on_equity || '',
      net_yield: new_record.net_yield || '',
      pre_tax_investment_return: new_record.pre_tax_investment_return || '',
      change_in_npw_and_dep: new_record.change_in_npw_and_dep || '',
      npw_and_dep_to_total_capital: new_record.npw_and_dep_to_total_capital || '',
      capital_adequacy_ratio: new_record.capital_adequacy_ratio || '',
      capital_and_surplus_to_liabilities: new_record.capital_and_surplus_to_liabilities || '',
      surplus_relief: new_record.surplus_relief || '',
      reinsurance_leverage: new_record.reinsurance_leverage || '',
      change_in_capital_surplus: new_record.change_in_capital_surplus || '',
      quick_liquidity: new_record.quick_liquidity || '',
      first_line_of_business: new_record.first_line_of_business || '',
      first_line_of_business_percent: new_record.first_line_of_business_percent || '',
      second_line_of_business: new_record.second_line_of_business || '',
      second_line_of_business_percent: new_record.second_line_of_business_percent || '',
      third_line_of_business: new_record.third_line_of_business || '',
      third_line_of_business_percent: new_record.third_line_of_business_percent || '',
      fourth_line_of_business: new_record.fourth_line_of_business || '',
      fourth_line_of_business_percent: new_record.fourth_line_of_business_percent || '',
      fifth_line_of_business: new_record.fifth_line_of_business || '',
      fifth_line_of_business_percent: new_record.fifth_line_of_business_percent || '',
      first_state_of_business: new_record.first_state_of_business || '',
      first_state_of_business_percent: new_record.first_state_of_business_percent || '',
      second_state_of_business: new_record.second_state_of_business || '',
      second_state_of_business_percent: new_record.second_state_of_business_percent || '',
      third_state_of_business: new_record.third_state_of_business || '',
      third_state_of_business_percent: new_record.third_state_of_business_percent || '',
      fourth_state_of_business: new_record.fourth_state_of_business || '',
      fourth_state_of_business_percent: new_record.fourth_state_of_business_percent || '',
      fifth_state_of_business: new_record.fifth_state_of_business || '',
      fifth_state_of_business_percent: new_record.fifth_state_of_business_percent || ''
    });
  }, [record]);

  const handleInputChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const handleDateChange = (name, date) => {
    setData({ ...data, [name]: date });
  };

  const save = () => {
    Api.saveAmbestRecord({ id: record.id, payload: data })
      .then(() => {
        onSave('save');
      })
      .catch(() => {
        onClose();
      });
  };

  return (
    <Drawer
      alignRight={true}
      footer={<Button onClick={save}>Save Changes</Button>}
      onClose={onClose}
      show={show}
      title='Editing AMBest Record'
    >
      <Field label='Carrier'>
        <Select
          onChange={handleInputChange.bind(null, 'carrier_id')}
          options={[{ value: '', label: 'Unassigned' }].concat(
            carriers.map(c => ({ value: c.id, label: c.name }))
          )}
          value={data.carrier_id}
        />
      </Field>

      <FieldGroup contained={true} label='Financial Strength'>
        <Field label='Current Financial Strength Rating'>
          <Input
            onChange={handleInputChange.bind(null, 'financial_strength_rating_current')}
            value={data.financial_strength_rating_current}
          />
        </Field>

        <Field label='Financial Strength Rating Effective Date:'>
          <DatePicker
            onChange={handleDateChange.bind(
              null,
              'financial_strength_rating_current_effective_date'
            )}
            value={data.financial_strength_rating_current_effective_date}
          />
        </Field>

        <Field label='Financial Strength Rating Outlook:'>
          <Input
            onChange={handleInputChange.bind(null, 'financial_strength_rating_outlook')}
            value={data.financial_strength_rating_outlook}
          />
        </Field>

        <Field label='Financial Strength Category'>
          <Input
            onChange={handleInputChange.bind(null, 'financial_strength_category')}
            value={data.financial_strength_category}
          />
        </Field>

        <Field label='Long Term Issuer Rating'>
          <Input
            onChange={handleInputChange.bind(null, 'long_term_issuer_rating_current')}
            value={data.long_term_issuer_rating_current}
          />
        </Field>

        <Field label='Long Term Issuer Rating Effective Date:'>
          <DatePicker
            onChange={handleDateChange.bind(null, 'long_term_issuer_rating_current_effective_date')}
            value={data.long_term_issuer_rating_current_effective_date}
          />
        </Field>

        <Field label='Enter Long Term Issuer Rating Outlook'>
          <Input
            onChange={handleInputChange.bind(null, 'long_term_issuer_rating_current_outlook')}
            value={data.long_term_issuer_rating_current_outlook}
          />
        </Field>
      </FieldGroup>

      <FieldGroup contained={true} label='Carrier Information'>
        <Field label='AMBest Carrier Name'>
          <Input
            onChange={handleInputChange.bind(null, 'ambest_carrier_name')}
            value={data.ambest_carrier_name}
          />
        </Field>

        <Field label='Street Address'>
          <Input
            onChange={handleInputChange.bind(null, 'street_address')}
            value={data.street_address}
          />
        </Field>

        <div style={{ display: 'flex' }}>
          <Field label='City' style={{ width: 200 }}>
            <Input onChange={handleInputChange.bind(null, 'city')} value={data.city} />
          </Field>

          <Field label='State' style={{ width: 200, margin: '0px 10px' }}>
            <Select
              onChange={handleInputChange.bind(null, 'state')}
              options={STATES}
              value={data.state}
            />
          </Field>

          <Field label='Zip Code'>
            <Input onChange={handleInputChange.bind(null, 'zip_code')} value={data.zip_code} />
          </Field>
        </div>

        <Field label='Phone Number'>
          <Input
            format='phone'
            onChange={handleInputChange.bind(null, 'phone_number')}
            value={data.phone_number}
          />
        </Field>
      </FieldGroup>

      <FieldGroup contained={true} label='Balance Sheet Data'>
        <Field label='Bonds (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'bonds')}
            type='number'
            value={data.bonds}
          />
        </Field>

        <Field label='Mortgage Loans and Real Estate (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'mortgage_loans_and_real_estate_percent')}
            type='number'
            value={data.mortgage_loans_and_real_estate_percent}
          />
        </Field>

        <Field label='Common and Preferred Stock (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'common_and_preferred_stock_percent')}
            type='number'
            value={data.common_and_preferred_stock_percent}
          />
        </Field>

        <Field label='Other Assets (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'other_assets_percent')}
            type='number'
            value={data.other_assets_percent}
          />
        </Field>

        <Field label='Total Assets ($000)'>
          <Input
            format='currency'
            onChange={handleInputChange.bind(null, 'total_assets')}
            value={data.total_assets}
          />
        </Field>

        <Field label='Life Reserves (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'life_reserves_percent')}
            type='number'
            value={data.life_reserves_percent}
          />
        </Field>

        <Field label='Health Reserves (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'health_reserves')}
            type='number'
            value={data.health_reserves}
          />
        </Field>

        <Field label='Annuity Reserves and Deposit Liabilities (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'annuity_reserves_and_deposit_liabilities')}
            type='number'
            value={data.annuity_reserves_and_deposit_liabilities}
          />
        </Field>

        <Field label='Other Liabilities ($000)'>
          <Input
            format='currency'
            onChange={handleInputChange.bind(null, 'other_liabilities')}
            value={data.other_liabilities}
          />
        </Field>

        <Field label='Capital and Surplus ($000)'>
          <Input
            format='currency'
            onChange={handleInputChange.bind(null, 'capital_and_surplus')}
            value={data.capital_and_surplus}
          />
        </Field>

        <Field label='Direct Premiums Written ($000)'>
          <Input
            format='currency'
            onChange={handleInputChange.bind(null, 'direct_premiums_written')}
            value={data.direct_premiums_written}
          />
        </Field>

        <Field label='Net Premiums Written and Deposits ($000)'>
          <Input
            format='currency'
            onChange={handleInputChange.bind(null, 'net_premiums_written_and_deposits')}
            value={data.net_premiums_written_and_deposits}
          />
        </Field>

        <Field label='Operating Cash Flow ($000)'>
          <Input
            format='currency'
            onChange={handleInputChange.bind(null, 'operating_cash_flow')}
            value={data.operating_cash_flow}
          />
        </Field>

        <Field label='Nog Before Tax ($000)'>
          <Input
            format='currency'
            onChange={handleInputChange.bind(null, 'nog_before_taxes')}
            value={data.nog_before_taxes}
          />
        </Field>

        <Field label='Nog After Tax ($000)'>
          <Input
            format='currency'
            onChange={handleInputChange.bind(null, 'nog_after_taxes')}
            value={data.nog_after_taxes}
          />
        </Field>

        <Field label='Net Income ($000) '>
          <Input
            format='currency'
            onChange={handleInputChange.bind(null, 'net_income')}
            value={data.net_income}
          />
        </Field>

        <Field label='Unrealized Capital Gains ($000) '>
          <Input
            format='currency'
            onChange={handleInputChange.bind(null, 'unrealized_capital_gains')}
            value={data.unrealized_capital_gains}
          />
        </Field>

        <Field label='Benefit Paid to NPW and Dep (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'benefit_paid_to_npw_and_dep')}
            type='number'
            value={data.benefit_paid_to_npw_and_dep}
          />
        </Field>

        <Field label='Commissions and Expenses to NPW and Deposits (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'commissions_and_expenses_to_npw_and_deposit')}
            type='number'
            value={data.commissions_and_expenses_to_npw_and_deposit}
          />
        </Field>

        <Field label='NOG to Total Assets (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'nog_to_total_assets')}
            type='number'
            value={data.nog_to_total_assets}
          />
        </Field>

        <Field label='NOG to Total Revenue (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'nog_to_total_revenue')}
            type='number'
            value={data.nog_to_total_revenue}
          />
        </Field>

        <Field label='Net Yield (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'operating_return_on_equity')}
            type='number'
            value={data.operating_return_on_equity}
          />
        </Field>

        <Field label='Benefit Paid to NPW and Dep (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'net_yield')}
            type='number'
            value={data.net_yield}
          />
        </Field>

        <Field label='Pre-Tax Total Investment Return (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'pre_tax_investment_return')}
            type='number'
            value={data.pre_tax_investment_return}
          />
        </Field>

        <Field label='Change in NPW and DEP (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'change_in_npw_and_dep')}
            type='number'
            value={data.change_in_npw_and_dep}
          />
        </Field>

        <Field label='NPW and DEP to Total Capital (X)'>
          <Input
            onChange={handleInputChange.bind(null, 'npw_and_dep_to_total_capital')}
            type='number'
            value={data.npw_and_dep_to_total_capital}
          />
        </Field>

        <Field label='Capital Adequacy Ratio'>
          <Input
            onChange={handleInputChange.bind(null, 'capital_adequacy_ratio')}
            type='number'
            value={data.capital_adequacy_ratio}
          />
        </Field>

        <Field label='Capital and Surplus to Liabilities (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'capital_and_surplus_to_liabilities')}
            type='number'
            value={data.capital_and_surplus_to_liabilities}
          />
        </Field>

        <Field label='Surplus Relief (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'surplus_relief')}
            type='number'
            value={data.surplus_relief}
          />
        </Field>

        <Field label='Reinsurance Leverage (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'reinsurance_leverage')}
            type='number'
            value={data.reinsurance_leverage}
          />
        </Field>

        <Field label='Change in Capital Surplus (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'change_in_capital_surplus')}
            type='number'
            value={data.change_in_capital_surplus}
          />
        </Field>

        <Field label='Quick Liquidity (%)'>
          <Input
            onChange={handleInputChange.bind(null, 'quick_liquidity')}
            type='number'
            value={data.quick_liquidity}
          />
        </Field>
      </FieldGroup>

      <FieldGroup contained={true} label='Line of Business'>
        <div style={{ display: 'flex' }}>
          <Field label='First Line of Business' style={{ width: '70%', marginRight: 10 }}>
            <Input
              onChange={handleInputChange.bind(null, 'first_line_of_business')}
              value={data.first_line_of_business}
            />
          </Field>
          <Field label='Percentage (%)'>
            <Input
              onChange={handleInputChange.bind(null, 'first_line_of_business_percent')}
              type='number'
              value={data.first_line_of_business_percent}
            />
          </Field>
        </div>

        <div style={{ display: 'flex' }}>
          <Field label='Second Line of Business' style={{ width: '70%', marginRight: 10 }}>
            <Input
              onChange={handleInputChange.bind(null, 'second_line_of_business')}
              value={data.second_line_of_business}
            />
          </Field>
          <Field label='Percentage (%)'>
            <Input
              onChange={handleInputChange.bind(null, 'second_line_of_business_percent')}
              type='number'
              value={data.second_line_of_business_percent}
            />
          </Field>
        </div>

        <div style={{ display: 'flex' }}>
          <Field label='Third Line of Business' style={{ width: '70%', marginRight: 10 }}>
            <Input
              onChange={handleInputChange.bind(null, 'third_line_of_business')}
              value={data.third_line_of_business}
            />
          </Field>
          <Field label='Percentage (%)'>
            <Input
              onChange={handleInputChange.bind(null, 'third_line_of_business_percent')}
              type='number'
              value={data.third_line_of_business_percent}
            />
          </Field>
        </div>

        <div style={{ display: 'flex' }}>
          <Field label='Fourth Line of Business' style={{ width: '70%', marginRight: 10 }}>
            <Input
              onChange={handleInputChange.bind(null, 'fourth_line_of_business')}
              value={data.fourth_line_of_business}
            />
          </Field>
          <Field label='Percentage (%)'>
            <Input
              onChange={handleInputChange.bind(null, 'fourth_line_of_business_percent')}
              type='number'
              value={data.fourth_line_of_business_percent}
            />
          </Field>
        </div>

        <div style={{ display: 'flex' }}>
          <Field label='Fifth Line of Business' style={{ width: '70%', marginRight: 10 }}>
            <Input
              onChange={handleInputChange.bind(null, 'fifth_line_of_business')}
              value={data.fifth_line_of_business}
            />
          </Field>
          <Field label='Percentage (%)'>
            <Input
              onChange={handleInputChange.bind(null, 'fifth_line_of_business_percent')}
              type='number'
              value={data.fifth_line_of_business_percent}
            />
          </Field>
        </div>
      </FieldGroup>

      <FieldGroup contained={true} label='State of Business'>
        <div style={{ display: 'flex' }}>
          <Field label='First State of Business' style={{ width: '70%', marginRight: 10 }}>
            <Input
              onChange={handleInputChange.bind(null, 'first_state_of_business')}
              value={data.first_state_of_business}
            />
          </Field>
          <Field label='Percentage (%)'>
            <Input
              onChange={handleInputChange.bind(null, 'first_state_of_business_percent')}
              type='number'
              value={data.first_state_of_business_percent}
            />
          </Field>
        </div>

        <div style={{ display: 'flex' }}>
          <Field label='Second State of Business' style={{ width: '70%', marginRight: 10 }}>
            <Input
              onChange={handleInputChange.bind(null, 'second_state_of_business')}
              value={data.second_state_of_business}
            />
          </Field>
          <Field label='Percentage (%)'>
            <Input
              onChange={handleInputChange.bind(null, 'second_state_of_business_percent')}
              type='number'
              value={data.second_state_of_business_percent}
            />
          </Field>
        </div>

        <div style={{ display: 'flex' }}>
          <Field label='Third State of Business' style={{ width: '70%', marginRight: 10 }}>
            <Input
              onChange={handleInputChange.bind(null, 'third_state_of_business')}
              value={data.third_state_of_business}
            />
          </Field>
          <Field label='Percentage (%)'>
            <Input
              onChange={handleInputChange.bind(null, 'third_state_of_business_percent')}
              type='number'
              value={data.third_state_of_business_percent}
            />
          </Field>
        </div>

        <div style={{ display: 'flex' }}>
          <Field label='Fourth State of Business' style={{ width: '70%', marginRight: 10 }}>
            <Input
              onChange={handleInputChange.bind(null, 'fourth_state_of_business')}
              value={data.fourth_state_of_business}
            />
          </Field>
          <Field label='Percentage (%)'>
            <Input
              onChange={handleInputChange.bind(null, 'fourth_state_of_business_percent')}
              type='number'
              value={data.fourth_state_of_business_percent}
            />
          </Field>
        </div>

        <div style={{ display: 'flex' }}>
          <Field label='Fifth State of Business' style={{ width: '70%', marginRight: 10 }}>
            <Input
              onChange={handleInputChange.bind(null, 'fifth_state_of_business')}
              value={data.fifth_state_of_business}
            />
          </Field>
          <Field label='Percentage (%)'>
            <Input
              onChange={handleInputChange.bind(null, 'fifth_state_of_business_percent')}
              type='number'
              value={data.fifth_state_of_business_percent}
            />
          </Field>
        </div>
      </FieldGroup>
    </Drawer>
  );
};

Edit.propTypes = {
  carriers: PropTypes.array,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  record: PropTypes.object,
  show: PropTypes.bool
};

export default Edit;
